/* eslint-disable no-undef */
class CommunityRecentPosts extends HTMLElement {
  constructor () {
    super()
    this.observer = null
    this.resizeTimer = null
    this.isMobile = false
  }

  connectedCallback () {
    this.init()
  }

  disconnectedCallback () {
    this.cleanup()
  }

  init () {
    this.checkBreakpoint()

    if (window.IntersectionObserver) {
      if (this.isMobile) {
        this.initMobileAnimations()
      } else {
        this.initDesktopAnimations()
      }
    }

    this.handleResize()
    this.initClickablePostListeners()
  }

  checkBreakpoint () {
    this.isMobile = window.matchMedia('(max-width: 767px)').matches
  }

  initMobileAnimations () {
    const mobileItems = this.querySelectorAll('.community-recent-posts__mobile-item')

    if (mobileItems.length === 0) return

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('community-recent-posts__mobile-item--visible')) {
          // Add 400ms delay before animation
          setTimeout(() => {
            entry.target.classList.add('community-recent-posts__mobile-item--visible')
          }, 400)
          // Unobserve once triggered (one-time animation)
          this.observer.unobserve(entry.target)
        }
      })
    }, observerOptions)

    mobileItems.forEach(item => {
      this.observer.observe(item)
    })
  }

  initDesktopAnimations () {
    const desktopItems = this.querySelectorAll('.community-recent-posts__desktop-item')

    if (desktopItems.length === 0) return

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('community-recent-posts__desktop-item--visible')) {
          // Add 400ms delay before animation
          setTimeout(() => {
            entry.target.classList.add('community-recent-posts__desktop-item--visible')
          }, 400)
          // Unobserve once triggered (one-time animation)
          this.observer.unobserve(entry.target)
        }
      })
    }, observerOptions)

    desktopItems.forEach(item => {
      this.observer.observe(item)
    })
  }

  handleResize () {
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        this.checkBreakpoint()

        if (window.IntersectionObserver) {
          // Clean up existing observer
          this.cleanup()

          // Initialize appropriate animations based on new breakpoint
          if (this.isMobile) {
            this.initMobileAnimations()
          } else {
            this.initDesktopAnimations()
          }
        }
      }, 250)
    })
  }

  initClickablePostListeners () {
    const clickablePosts = this.querySelectorAll('.community-recent-posts__clickable-post')
    clickablePosts.forEach(post => {
      post.addEventListener('click', this.handlePostClick.bind(this))
    })
  }

  handlePostClick (event) {
    const post = event.currentTarget
    const externalUrl = post.dataset.externalUrl
    const modalTitle = post.dataset.modalTitle
    const modalContent = post.dataset.modalContent
    const cancelText = post.dataset.cancelText
    const proceedText = post.dataset.proceedText

    if (!externalUrl) return

    this.openExternalLinkModal(externalUrl, modalTitle, modalContent, cancelText, proceedText)
  }

  openExternalLinkModal (externalUrl, modalTitle, modalContent, cancelText, proceedText) {
    // Open modal with standard header configuration
    window.dispatchEvent(new CustomEvent('open:modal', {
      detail: {
        width: '500px',
        bodyClassAdd: ['px-6', 'pt-4'],
        footerClassAdd: ['md:px-6', 'md:pb-6', 'flex', 'gap-3', 'justify-center', 'w-full'],
        footerClassRemove: ['hidden', 'md:pb-0'],
      },
    }))

    // Wait for modal to be ready and populate content
    const checkModalReady = () => {
      const modal = document.querySelector('reusable-modal')
      const modalTitleElement = modal?.querySelector('.reusable-modal__title')
      const modalBody = modal?.querySelector('.reusable-modal__body')
      const modalFooter = modal?.querySelector('.reusable-modal__footer')

      if (!modal || !modalTitleElement || !modalBody || !modalFooter) {
        requestAnimationFrame(checkModalReady)
        return
      }

      // Set modal title
      modalTitleElement.textContent = modalTitle

      // Set body content
      modalBody.innerHTML = `
        <div class="external-link-warning tb-in-page-content text-center text-pretty">
          ${modalContent}
        </div>
      `

      // Set footer with buttons
      modalFooter.innerHTML = `
        <button type="button" class="external-link-cancel button button-outline flex-grow" aria-label="Cancel">
          ${cancelText}
        </button>
        <a href="${externalUrl}" target="_blank" rel="noopener noreferrer" class="external-link-proceed inline-flex justify-center text-center place-items-center w-fit rounded px-3 py-2 md:px-4 md:py-3 text-white bg-gradient-to-r from-[#11affe] to-[#0062e1] ring-offset-2 hover:ring-2 ring-[#11affe] flex-grow">
          ${proceedText}
        </a>
      `

      // Add event listeners for footer buttons
      const cancelButton = modalFooter.querySelector('.external-link-cancel')
      const proceedButton = modalFooter.querySelector('.external-link-proceed')

      if (cancelButton) {
        cancelButton.addEventListener('click', () => {
          window.dispatchEvent(new CustomEvent('close:modal'))
        })
      }

      if (proceedButton) {
        proceedButton.addEventListener('click', () => {
          // Link will open in new tab, also close modal
          window.dispatchEvent(new CustomEvent('close:modal'))
        })
      }
    }

    checkModalReady()
  }

  cleanup () {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }
  }
}

if (!customElements.get('community-recent-posts')) {
  customElements.define('community-recent-posts', CommunityRecentPosts)
}
